import { faCircleMinus } from "@fortawesome/sharp-solid-svg-icons"
import type { PrecomputedBiothreatSignal } from "./types"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import {
  Box,
  Card,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { gridClasses, type GridColDef } from "@mui/x-data-grid-pro"
import type React from "react"
import { useMemo, useState } from "react"
import { downloadFile } from "../../utils/downloadFile"
import { extraColors } from "../../utils/theme"
import { BaseGrid } from "../BaseGrid/BaseGrid"
import { sharedGridSx } from "../AlertCuration/AlertCurationShared"
import { ActionCell } from "../AlertCuration/AlertCurationTableColumns/SharedAlertTableColumns"
import { usePrecomputedBiothreatSignals } from "./PrecomputedBiothreatSignalsContext"
import { precomputedGridSx } from "./PrecomputedSharedColumns"
import { exportPrecomputedSignalsToCsv } from "./exportPrecomputedSignalsToCsv"

/**
 * The pinned-signals collapsible. Shared by the classic page and the v2
 * console — pass `variant="command"` to render it against the dark console
 * surface. Underlying data/columns/export behavior is identical in both.
 */
export const PrecomputedSelectedSignals: React.FC<{
  variant?: "default" | "command"
}> = ({ variant = "default" }) => {
  const isCommand = variant === "command"
  const {
    pinnedSignals,
    removePinnedSignal,
    selectedSignalsExpanded,
    setSelectedSignalsExpanded,
  } = usePrecomputedBiothreatSignals()
  const rows = useMemo(
    () => Array.from(pinnedSignals.values()),
    [pinnedSignals]
  )

  const columns = useMemo(
    (): GridColDef<PrecomputedBiothreatSignal>[] => [
      {
        field: "action",
        headerName: "",
        width: 48,
        sortable: false,
        align: "center",
        headerAlign: "center",
        cellClassName: "precomputed-selected-action-cell",
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <ActionCell
              actions={[
                {
                  onAction: signal => removePinnedSignal(signal.id),
                  icon: faCircleMinus,
                  tooltip: "Remove from Selected Signals",
                  color: extraColors.status.redLight,
                },
              ]}
              row={row}
            />
          </Box>
        ),
      },
      { field: "source", headerName: "Source", width: 80 },
      { field: "title", headerName: "Title", flex: 1, minWidth: 200 },
      { field: "resolved_location", headerName: "Location", width: 140 },
      {
        field: "disease",
        headerName: "Disease",
        width: 120,
        valueGetter: (_, row) => row.clean_disease_codes[0] ?? "",
      },
    ],
    [removePinnedSignal]
  )

  const handleExport = (event: React.MouseEvent) => {
    event.stopPropagation()
    const csv = exportPrecomputedSignalsToCsv(rows)
    downloadFile(
      new Blob([csv], { type: "text/csv" }),
      `precomputed-biothreat-signals-${Date.now()}.csv`
    )
  }

  // Expanded/collapsed state is persisted in context (collapsed by default).
  // Auto-expand whenever a signal is newly added, so a just-pinned row is
  // visible even if the panel was collapsed. Render-phase "adjust state when a
  // value changes" pattern (no effect).
  const [prevCount, setPrevCount] = useState(rows.length)
  if (rows.length !== prevCount) {
    setPrevCount(rows.length)
    if (rows.length > prevCount && !selectedSignalsExpanded) {
      setSelectedSignalsExpanded(true)
    }
  }

  const hasSignals = rows.length > 0
  const isOpen = selectedSignalsExpanded

  return (
    <Card
      sx={{
        p: 2,
        bgcolor: isCommand ? "rgba(18,24,33,0.74)" : extraColors.translucent5,
        border: `1px solid ${
          isCommand ? "rgba(255,255,255,0.13)" : extraColors.translucent8
        }`,
        borderRadius: isCommand ? "8px" : undefined,
        backgroundImage: "none",
      }}
    >
      <Stack
        direction="row"
        onClick={() => setSelectedSignalsExpanded(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            setSelectedSignalsExpanded(!isOpen)
          }
        }}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: isOpen ? 1 : 0,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
          <ExpandMoreIcon
            sx={{
              color: extraColors.white,
              transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: theme =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          />
          <Typography variant="h3Bold" color={extraColors.white}>
            Selected Signals ({rows.length})
          </Typography>
        </Stack>
        <IconButton
          onClick={handleExport}
          disabled={!hasSignals}
          aria-label="Export CSV"
        >
          <FileDownloadIcon />
        </IconButton>
      </Stack>
      <Collapse in={isOpen} unmountOnExit>
        <Box sx={{ minHeight: 160 }}>
          <BaseGrid
            rows={rows}
            columns={columns}
            checkbox={false}
            density="compact"
            pagination={false}
            hideFooter
            sx={{
              ...(isCommand ? precomputedGridSx : sharedGridSx),
              minHeight: 160,
              [`& .${gridClasses.cell}.precomputed-selected-action-cell`]: {
                padding: "0 4px",
              },
            }}
          />
        </Box>
      </Collapse>
    </Card>
  )
}
